import React, { useState, useEffect } from 'react';
import { apiFetch, useToast } from '../../App';
import { Plus, Edit2, X, Check, Syringe, FileText } from 'lucide-react';

export default function PawsPets() {
  const addToast = useToast();
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPet, setSelectedPet] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', species: 'DOG', breed: '', size: 'MEDIUM', birthDate: '', allergies: '', behaviorNotes: '' });

  useEffect(() => {
    apiFetch('/api/pets').then(p => { setPets(p); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    try {
      if (selectedPet) {
        const updated = await apiFetch(`/api/pets/${selectedPet.id}`, { method: 'PUT', body: JSON.stringify(form) });
        setPets(pets.map(p => p.id === selectedPet.id ? { ...p, ...updated } : p));
        setSelectedPet(null);
        addToast('Pet updated!');
      } else {
        const newPet = await apiFetch('/api/pets', { method: 'POST', body: JSON.stringify(form) });
        setPets([...pets, newPet]);
        setShowAdd(false);
        addToast(`${form.name} added!`);
      }
      setForm({ name: '', species: 'DOG', breed: '', size: 'MEDIUM', birthDate: '', allergies: '', behaviorNotes: '' });
    } catch (e) { addToast(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`/api/pets/${id}`, { method: 'DELETE' });
      setPets(pets.filter(p => p.id !== id));
      setSelectedPet(null);
      addToast('Pet removed');
    } catch (e) { addToast(e.message, 'error'); }
  };

  const editPet = (pet) => {
    setSelectedPet(pet);
    setForm({ name: pet.name, species: pet.species, breed: pet.breed || '', size: pet.size || 'MEDIUM', birthDate: pet.birthDate || '', allergies: pet.allergies || '', behaviorNotes: pet.behaviorNotes || '' });
  };

  return (
    <div data-testid="paws-pets" className="max-w-4xl">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>My Pets</h1>
        <button onClick={() => { setShowAdd(true); setSelectedPet(null); setForm({ name: '', species: 'DOG', breed: '', size: 'MEDIUM', birthDate: '', allergies: '', behaviorNotes: '' }); }} className="btn-primary text-sm" data-testid="add-pet-btn">
          <Plus size={16} /> Add Pet
        </button>
      </div>

      {/* Pet Cards */}
      <div className="grid sm:grid-cols-2 gap-6 mb-8">
        {pets.map(pet => (
          <div key={pet.id} className="petfolk-card p-6" data-testid={`pet-${pet.id}`}>
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-petfolk-orange/20 to-petfolk-purple/10 flex items-center justify-center text-3xl">
                  {pet.species === 'DOG' ? '🐕' : '🐈'}
                </div>
                <div>
                  <h3 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{pet.name}</h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{pet.breed || pet.species} {pet.size && `• ${pet.size}`}</p>
                  {pet.birthDate && <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Born: {new Date(pet.birthDate).toLocaleDateString('en-IN')}</p>}
                </div>
              </div>
              <button onClick={() => editPet(pet)} className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5" data-testid={`edit-pet-${pet.id}`}>
                <Edit2 size={16} style={{ color: 'var(--text-secondary)' }} />
              </button>
            </div>
            {pet.allergies && (
              <div className="text-xs px-3 py-1.5 rounded-xl mb-2" style={{ background: 'rgba(255,107,107,0.1)', color: '#DC2626' }}>
                Allergies: {pet.allergies}
              </div>
            )}
            {pet.behaviorNotes && (
              <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>Notes: {pet.behaviorNotes}</p>
            )}
            {/* Vaccination Records */}
            {pet.vaccinationRecord?.length > 0 && (
              <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
                <div className="flex items-center gap-1 mb-2">
                  <Syringe size={14} className="text-petfolk-purple" />
                  <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>Vaccinations</span>
                </div>
                {pet.vaccinationRecord.map((v, i) => (
                  <div key={i} className="flex justify-between text-xs py-1">
                    <span style={{ color: 'var(--text-secondary)' }}>{v.vaccine}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>Next: {v.nextDue}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {(showAdd || selectedPet) && (
        <div className="modal-overlay" onClick={() => { setShowAdd(false); setSelectedPet(null); }}>
          <div className="petfolk-card w-full max-w-md mx-4 p-6 animate-slide-up" onClick={e => e.stopPropagation()} data-testid="pet-form-modal">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{selectedPet ? 'Edit Pet' : 'Add New Pet'}</h2>
              <button onClick={() => { setShowAdd(false); setSelectedPet(null); }}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <input className="petfolk-input" placeholder="Pet name *" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="pet-form-name" />
              <div className="flex gap-2">
                {['DOG', 'CAT'].map(s => (
                  <button key={s} onClick={() => setForm({...form, species: s})} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold ${form.species === s ? 'bg-petfolk-orange text-white' : ''}`} style={{ border: `1px solid var(--border-color)`, color: form.species !== s ? 'var(--text-secondary)' : undefined }}>
                    {s === 'DOG' ? '🐕 Dog' : '🐈 Cat'}
                  </button>
                ))}
              </div>
              <input className="petfolk-input" placeholder="Breed" value={form.breed} onChange={e => setForm({...form, breed: e.target.value})} data-testid="pet-form-breed" />
              <div className="flex gap-2">
                {['SMALL', 'MEDIUM', 'LARGE'].map(sz => (
                  <button key={sz} onClick={() => setForm({...form, size: sz})} className={`flex-1 py-2.5 rounded-xl text-xs font-semibold ${form.size === sz ? 'bg-petfolk-purple text-white' : ''}`} style={{ border: `1px solid var(--border-color)`, color: form.size !== sz ? 'var(--text-secondary)' : undefined }}>
                    {sz} {sz === 'SMALL' ? '(<10kg)' : sz === 'MEDIUM' ? '(10-25kg)' : '(>25kg)'}
                  </button>
                ))}
              </div>
              <input className="petfolk-input" type="date" placeholder="Birth date" value={form.birthDate} onChange={e => setForm({...form, birthDate: e.target.value})} data-testid="pet-form-birthdate" />
              <input className="petfolk-input" placeholder="Allergies (if any)" value={form.allergies} onChange={e => setForm({...form, allergies: e.target.value})} />
              <textarea className="petfolk-input" placeholder="Behavior notes (for groomer reference)" value={form.behaviorNotes} onChange={e => setForm({...form, behaviorNotes: e.target.value})} rows={2} />
              <div className="flex gap-3">
                {selectedPet && (
                  <button onClick={() => handleDelete(selectedPet.id)} className="text-red-500 text-sm font-semibold" data-testid="delete-pet-btn">Delete Pet</button>
                )}
                <button onClick={handleSave} className="btn-primary ml-auto" data-testid="save-pet-btn">
                  <Check size={16} /> {selectedPet ? 'Update' : 'Add Pet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
